import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ErrorHandler } from '@shared/services/errorHandler';
import { isSessionExpired } from '@shared/services/accountService';
import { linkRegistry } from '@shared/services/hateoas/linkRegistry';
import { SessionExpiredError } from '@shared/utils/SessionExpiredError';
import { isUnauthorizedError } from './unauthorized';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { inlineError?: boolean; context?: string };
  }
}

export interface AuthStatus {
  authenticated: boolean;
}

export const AUTH_STATUS_KEY = ['auth', 'status'] as const;

const isAuthKey = (key: readonly unknown[]): boolean => key[0] === 'auth';

export const endSession = (): void => {
  void queryClient.cancelQueries({ predicate: (query) => !isAuthKey(query.queryKey) });
  queryClient.removeQueries({ predicate: (query) => !isAuthKey(query.queryKey) });
  linkRegistry.reset();
  queryClient.setQueryData<AuthStatus>(AUTH_STATUS_KEY, { authenticated: false });
};

export const handleSessionError = async (error: unknown): Promise<void> => {
  if (error instanceof SessionExpiredError) {
    endSession();
    return;
  }
  if (isUnauthorizedError(error as { status?: number }) && (await isSessionExpired())) {
    endSession();
  }
};

export const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      void handleSessionError(error);
      ErrorHandler.handle(error, query.queryKey.join('/'), { notify: false });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      void handleSessionError(error);
      const context = mutation.meta?.context ?? mutation.options.mutationKey?.join('/');
      ErrorHandler.handle(error, context, { notify: !mutation.meta?.inlineError });
    },
  }),
});
